import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatAlt2Outline, ChatEmptyOutline, PlusCircleOutline, theme, useMediaQuery } from '@bonusly/design-system';

import { Bonus } from '../../../../../../types/bonus';
import { useActions } from './use-actions';

interface BonusCardActionsProps {
	bonus: Bonus;
	onClickAddOn: VoidFunction;
	onClickComment: VoidFunction;
	onClickRequestFeedback: VoidFunction;
}

interface ActionData {
	action: VoidFunction;
	label: string;
	color: string;
	icon: FC;
	text: string;
}

export const useBonusCardActions = ({ bonus, onClickAddOn, onClickComment, onClickRequestFeedback }: BonusCardActionsProps) => {
	const { t } = useTranslation('recognition');
	const { displayAddOn, displayFeedback, displayThanks } = useActions(bonus);
	const [visibleAddOn, visibleFeedback] = useMediaQuery([`(min-width: ${theme.breakpoints.sm})`, `(min-width: ${theme.breakpoints.xs})`]);

	return useMemo(() => {
		// Add a comment is always the first action and always visible
		const actions: ActionData[] = [
			{
				action: onClickComment,
				label: t('comment.add_comment_button'),
				color: 'gray',
				icon: ChatEmptyOutline,
				text: displayThanks ? t('bonus_card.thanks_button') : t('bonus_card.comment_button'),
			},
		];

		const menuOptions: ActionData[] = [];

		// Display xxx is whether or not the action is available in its entirety
		if (displayFeedback) {
			// Visible xxx is whether or not the action is visible at the current breakpoint, and if
			// not is collapsed into the menu
			(visibleFeedback ? actions : menuOptions).push({
				action: onClickRequestFeedback,
				label: t('bonus_card.feedback.get_feedback_button'),
				color: 'gray',
				icon: ChatAlt2Outline,
				text: t('bonus_card.feedback.get_feedback_button'),
			});
		}

		if (displayAddOn) {
			(visibleAddOn ? actions : menuOptions).push({
				action: onClickAddOn,
				label: t('bonus_card.add_on_button'),
				color: 'green',
				icon: PlusCircleOutline,
				text: t('bonus_card.add_on_button'),
			});
		}

		return {
			actions,
			displayMenu: !!menuOptions.length,
			menuOptions,
		};
	}, [displayAddOn, displayFeedback, displayThanks, onClickAddOn, onClickComment, onClickRequestFeedback, t, visibleAddOn, visibleFeedback]);
};
